import { useId } from 'react';
import { Link } from 'react-router-dom';
import { RepeatIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { usePages } from '../../contexts/SiteContext';
import { useFeatures } from '../../hooks/useFeatures';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnOnClay } from '../../ui/styles';
import { Section } from './Section';
type Props = {
  /**
   * `full`: het hele verhaal met alle voordelen, op klei: het ene diepe vlak van de webshop.
   * `short`: dezelfde woorden zonder de voordelenlijst, licht (shell), voor pagina's die hun klei
   * al elders gebruiken (Protocollen). Nooit anders geformuleerd: alles komt uit Paginateksten › Abonnement.
   */
  size?: 'full' | 'short';
};
const CTA = 'Stel je abonnement samen';

/** Het abonnement: één bron voor elke pagina. Op klei alleen `text-shell` (5,6:1); blush alleen voor iconen. */
export function SubscriptionBlock({
  size = 'full'
}: Props) {
  const {
    subscription
  } = usePages();
  const headingId = useId();
  // Abonnement uitgezet (Instellingen › Koppelingen & privacy › Onderdelen): het blok staat nergens.
  const {
    subscriptions
  } = useFeatures();
  if (!subscriptions) return null;
  if (size === 'short') {
    return <section className="bg-shell py-16 sm:py-20" aria-labelledby={headingId} data-id="element-8827" data-mp="5of032tq2ji9">
        <div className="mx-auto grid max-w-content gap-6 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-8828" data-mp="9nxc193kfk8y">
          <div className="lg:col-span-8" data-id="element-8829" data-mp="xfktz1iunkz3">
            <p className="flex items-center gap-2 text-[14px] text-clay" data-id="element-8830" data-mp="xgm7dcnc8jjz">
              <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8831" data-mp="12cl0fdjm2pqq" />
              {subscription.name}
            </p>
            <h2 id={headingId} className="mt-4 type-sub text-ink" data-id="element-8832" data-mp="snjk1m95m6c8">
              {subscription.claim}
            </h2>
            <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-8833" data-mp="1gclzqok89p43">{subscription.intro}</p>
            <p className="mt-3 text-[14px] text-muted" data-id="element-8834" data-mp="1l442ni5t9zrp">
              {subscription.price} · {subscription.smallPrint}
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:justify-end" data-id="element-8835" data-mp="1uxqf7upsk1qv">
            <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-8836" data-mp="1qzaigz3wrf09">
              {CTA}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8837" data-mp="1ly3d16p8mrwr" />
            </Link>
          </div>
        </div>
      </section>;
  }
  return <Section tone="clay" labelledBy={headingId} contained={false} data-id="element-8838" data-mp="1c0qav63z3b0j">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8839" data-mp="1c33269vmexq9">
        <div className="lg:col-span-5" data-id="element-8840" data-mp="1785jeb8jha06">
          <p className="flex items-center gap-2 text-[14px] text-shell" data-id="element-8841" data-mp="13855crpcr0tn">
            <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8842" data-mp="136ahp5mp4ulv" />
            {subscription.name}
          </p>
          <h2 id={headingId} className={`mt-5 ${sectionHeading}`} data-id="element-8843" data-mp="tb0ggk44gr95">
            {subscription.claim}
          </h2>
          <p className="mt-6 max-w-md text-[17px] leading-relaxed text-shell" data-id="element-8844" data-mp="ocff2hd4w1j4">{subscription.intro}</p>
          <p className="mt-5 text-[14px] text-shell" data-id="element-8845" data-mp="e6fm269aophr">
            {subscription.price} · {subscription.smallPrint}
          </p>
          <Link to="/webshop" className={`mt-9 ${btnOnClay}`} data-id="element-8846" data-mp="j4pi0j2ufu4f">
            {CTA}
          </Link>
        </div>

        <ul className="grid self-center border-t border-shell/30 sm:grid-cols-2 lg:col-span-7" data-id="element-8847" data-mp="o3kc4h69h53u">
          {subscription.perks.map(perk => <li key={perk.title} className="border-b border-shell/30 py-7 sm:pr-8" data-id="element-8848" data-mp="3rn55avc8rh2n">
              <h3 className="flex items-start gap-2.5 type-name font-light text-shell" data-id="element-8849" data-mp="fj5xewk20rl">
                <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-blush" strokeWidth={2} aria-hidden="true" data-id="element-8850" data-mp="5ffapgd2tw87" />
                {perk.title}
              </h3>
              <p className="mt-2 pl-[1.625rem] text-[15px] leading-relaxed text-shell" data-id="element-8851" data-mp="a7f3mouqbova">{perk.body}</p>
            </li>)}
        </ul>
      </div>
    </Section>;
}